import React, { useEffect, useState, useCallback } from "react";
import { Bell, Check, MailCheck, FileText } from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const KIND_LABELS = {
  fault_p1: "P1-storing",
  morning_overview: "Ochtendoverzicht",
  appointment_reminder: "Herinnering",
  weekly_replacement_digest: "Vervangingslijst",
  test: "Testmail",
};

const kindLabel = (k) => KIND_LABELS[k] || "Melding";

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

// Minimal safety-net bell for owner/td: shows notifications already written to
// the notifications collection (P1 alerts, overviews, reminders, test mail) so
// they are seen even when the scheduler is off or mail is not yet verified.
export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (e) {
      // Silent on the poll; only surface if the user opened it.
      if (open) toast.error(getErrorMessage(e, "Kan meldingen niet laden."));
    } finally {
      setLoading(false);
    }
  }, [open]);

  // Initial load + light polling (60s) so the badge stays fresh.
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Refresh immediately when opened.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const markAllRead = async () => {
    setMarking(true);
    try {
      const { data: res } = await api.post("/notifications/mark-read", {});
      setData((d) => ({ items: d.items.map((i) => ({ ...i, read: true })), unread: res.unread }));
    } catch (e) {
      toast.error(getErrorMessage(e, "Markeren mislukt."));
    } finally {
      setMarking(false);
    }
  };

  const unread = data.unread || 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white hover:bg-console-diep hover:text-white"
          data-testid="notification-bell"
          aria-label={`Meldingen${unread ? `, ${unread} ongelezen` : ""}`}
        >
          <Bell size={20} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute right-0 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-label leading-none text-destructive-foreground"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      {/* Capped to the visual viewport: at 390×400 the old fixed-height list
          ran past the bottom edge. The list scrolls, the header stays. */}
      <PopoverContent align="end" className="flex max-h-[min(var(--radix-popover-content-available-height),24rem)] w-80 flex-col p-0" data-testid="notification-panel">
        <div className="flex shrink-0 items-center justify-between px-3 py-2.5">
          <span className="text-body font-semibold text-foreground">Meldingen</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-data"
            onClick={markAllRead}
            disabled={marking || unread === 0}
            data-testid="notification-mark-read"
          >
            <Check size={14} className="mr-1" /> Markeer als gelezen
          </Button>
        </div>
        <Separator />
        {/* The panel caps against the viewport: on a short phone screen a fixed
            20rem list ran off the bottom edge with no way to reach it. */}
        <ScrollArea className="min-h-0 flex-1">
          {loading && data.items.length === 0 ? (
            <p className="px-3 py-6 text-center text-body text-muted-foreground">Laden...</p>
          ) : data.items.length === 0 ? (
            <p className="px-3 py-8 text-center text-body text-muted-foreground" data-testid="notification-empty">
              Geen meldingen.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {data.items.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={`px-3 py-2.5 ${n.read ? "" : "bg-accent/40"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      {!n.read && <span className="h-2 w-2 shrink-0 rounded bg-destructive" aria-hidden="true" />}
                      <span className="text-data font-medium text-foreground">{kindLabel(n.kind)}</span>
                    </div>
                    <span className="shrink-0 text-label text-muted-foreground">{fmt(n.created_at)}</span>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-body text-foreground">{n.subject}</p>
                  <div className="mt-1">
                    {n.mail_status === "verstuurd" ? (
                      <Badge variant="secondary" className="gap-1 text-label">
                        <MailCheck size={14} /> verstuurd
                      </Badge>
                    ) : n.mail_status === "mail uitgezet" ? (
                      <Badge variant="outline" className="gap-1 text-label" data-testid={`notification-mail-uit-${n.id}`}>
                        <FileText size={14} /> mail uitgezet
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-label">
                        <FileText size={14} /> alleen gelogd
                      </Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
